import React, { useState, useEffect } from "react";
import { Edit2, Check, X } from "lucide-react";
import IconButton from "./Button/IconButton.jsx";
import Input from "./input.jsx";
import CustomDatePicker from "./DatePicker.jsx";

/**
 * EditableField - Reusable inline editable field component
 * 
 * Supports text, date, and time inputs with inline editing functionality.
 * Uses lucide-react icons for consistency.
 * 
 * @param {Object} props
 * @param {string|Date} props.initialValue - Initial value to display
 * @param {Function} props.onSave - Callback when value is saved (receives new value)
 * @param {string} props.className - Additional CSS classes for the display value
 * @param {string} props.type - Field type: "text", "date", or "time" (default: "text")
 * @param {string} props.placeholder - Placeholder text for input (optional)
 */
const EditableField = ({
    initialValue,
    onSave,
    className = "",
    type = "text",
    placeholder = "",
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(initialValue);

    // Sync internal state with prop changes
    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const handleSave = () => {
        onSave(value);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setValue(initialValue);
        setIsEditing(false);
    };

    const datePickerRef = React.useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
                handleCancel();
            }
        };

        if (isEditing && (type === "date" || type === "time")) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isEditing, type]);

    // Handle date/time fields
    if (type === "date" || type === "time") {
        return (
            <div className="flex items-center gap-2 group relative" ref={datePickerRef}>
                <span className={className}>
                    {type === "date"
                        ? value.toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                        })
                        : value.toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                </span>
                <IconButton
                    icon={<Edit2 className="h-4 w-4" />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditing(true)}
                    className="opacity-5 group-hover:opacity-100 transition-opacity duration-200"
                />
                {isEditing && (
                    <div className="absolute z-10 bg-background border rounded-md p-2 shadow-lg">
                        <CustomDatePicker
                            selected={value}
                            onChange={(date) => {
                                setValue(date);
                                onSave(date);
                                setIsEditing(false);
                            }}
                            showTimeSelect={type === "time"}
                            showTimeSelectOnly={type === "time"}
                            timeIntervals={15}
                            dateFormat={type === "date" ? "MMMM d, yyyy" : "h:mm aa"}
                            inline
                        />
                    </div>
                )}
            </div>
        );
    }

    // Handle text fields
    return isEditing ? (
        <div className="flex items-center gap-1">
            <Input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className={`min-h-[1.75rem] h-auto py-1 ${className} px-2 w-fit`}
                placeholder={placeholder}
                autoFocus
                onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave();
                    if (e.key === 'Escape') handleCancel();
                }}
            />
            <div className="flex items-center">
                <IconButton
                    icon={<Check className="h-4 w-4" />}
                    size="sm"
                    variant="ghost"
                    onClick={handleSave}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    tooltip="Save"
                />
                <IconButton
                    icon={<X className="h-4 w-4" />}
                    size="sm"
                    variant="ghost"
                    onClick={handleCancel}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    tooltip="Cancel"
                />
            </div>
        </div>
    ) : (
        <div className="flex items-center gap-2 group">
            <span className={className}>{value || placeholder}</span>
            <IconButton
                icon={<Edit2 className="h-4 w-4" />}
                size="sm"
                variant="ghost"
                onClick={() => setIsEditing(true)}
                className="opacity-5 group-hover:opacity-100 transition-opacity duration-200"
            />
        </div>
    );
};

export default EditableField;
